import { useAppDispatch, useAppSelector } from "../../store/hooks";
import BlackRoundWrapper from "../UI/wrappers/ColouredRoundWrapper";
import CirclularStatusIcon from "./CirclularStatusIcon";
import StandardButton from "../UI/buttons/StandardButton";
import podooStatusBarStyles from "../../styles/statusBars.module.css";
import { ReactComponent as StarsIcon } from "../../assets/icons/stars_white.svg";
import { ReactComponent as XIcon } from "../../assets/icons/x.svg";
import React, { useEffect, useMemo, useState } from "react";
import { useTimeAgo } from "../../hooks/useTimeAgo";
import {
  PlanViewStateEntry,
  resetPondooViewStateByScenarioId,
} from "../../store/realTimeUpdates";
import {
  initiateOptimization,
  stopOptimization,
} from "../../store/ganttChart/action";
import IconButtonComponent from "../UI/buttons/IconButton";
import {
  determinePondooStatusBarState,
  getMaxWidth,
  getProgressBarValue,
  getTabLabel,
} from "../../utils/statusBarUtils";
import { PondooStatusBarObject } from "../../utils/statusBarStateObjects";
import ProgressBar from "./ProgressBar";
import { useSolutionPlanView } from "../../hooks/useSolutionPlanView";
import {
  setIsPondooPlanReady,
  setOptimizedStatusLabel,
} from "../../store/pondooStatusBar";

const PondooPlanStatusBar = () => {
  const dispatch = useAppDispatch();
  const { isPondooPlanReady } = useAppSelector(
    (state) => state.pondooStatusBarSlice
  );

  /**
   * Dynamic display state given the user's selection of filter and date
   */
  const planViewState = useSolutionPlanView("Optimised");

  // const planViewState: PlanViewStateEntry = { status: "ready" };

  const startedTimeAgo = useTimeAgo(planViewState?.job?.createdDateTime);
  const completedTimeAgo = useTimeAgo(planViewState?.job?.completedDateTime);
  const isOutdated = !!planViewState?.job?.outdatedDateTime;

  useEffect(() => {
    if (planViewState.status === "ready" && !isPondooPlanReady)
      dispatch(setIsPondooPlanReady(true));
    else if (isPondooPlanReady && planViewState.status !== "ready") {
      dispatch(setIsPondooPlanReady(false));
    }
  }, [planViewState.status]);

  const pondooStatusBarObject = useMemo(() => {
    return determinePondooStatusBarState(
      planViewState,
      completedTimeAgo,
      isOutdated
    );
  }, [planViewState, completedTimeAgo, isOutdated]);

  useEffect(() => {
    dispatch(
      setOptimizedStatusLabel(
        getTabLabel(planViewState.status, completedTimeAgo)
      )
    );
  }, [planViewState, completedTimeAgo, startedTimeAgo]);

  // Progress bar update based on job start time
  const [progressBarValue, setProgressBarValue] = useState<number>(0);
  useEffect(() => {
    if (startedTimeAgo && planViewState.status === "loading") {
      setProgressBarValue(getProgressBarValue(startedTimeAgo) ?? 0);
    }
  }, [planViewState, startedTimeAgo]);

  // Functions
  const updatePlan = (idScenario: number) => {
    dispatch(resetPondooViewStateByScenarioId(idScenario));
    dispatch(initiateOptimization(idScenario));
  };

  const buttonAction = () => {
    if (planViewState?.idScenario) {
      if (
        planViewState.status === "ready" ||
        planViewState.status === "error" ||
        planViewState.status === "missing"
      ) {
        updatePlan(planViewState.idScenario);
      }
    }
  };

  const abortOptimization = () => {
    if (planViewState?.idScenario) {
      if (planViewState.status === "loading") {
        dispatch(stopOptimization(planViewState.idScenario));
      }
    }
  };

  //Drawing the statusBar based on hover state
  const [unfoldStatusBar, setUnfoldStatusBar] = useState<boolean>(false);

  return (
    <BlackRoundWrapper
      bgColor="var(--col-interaction-secondary)"
      height="60px"
      width="unset"
      maxWidth={
        unfoldStatusBar && pondooStatusBarObject
          ? getMaxWidth(pondooStatusBarObject)
          : "60px"
      }
      classes={`${podooStatusBarStyles.nexusPlanInfo} ${unfoldStatusBar ? "justify-between" : "justify-start"} ${unfoldStatusBar && podooStatusBarStyles.nexusPlanInfoUnfolded}`}
      onMouseEnter={() => {
        setUnfoldStatusBar(true);
      }}
      onMouseLeave={() => {
        setUnfoldStatusBar(false);
      }}
    >
      {pondooStatusBarObject && (
        <>
          <div className="flex items-center gap-[10px] justofy-start">
            <span className="flex items-center justify-center w-[30px]">
              <CirclularStatusIcon status={pondooStatusBarObject?.status} />
            </span>
            <p
              className={`${unfoldStatusBar ? "visible" : "invisible"} text-nowrap`}
            >
              {pondooStatusBarObject.title}
            </p>
          </div>
          {pondooStatusBarObject.buttonTitle && (
            <StandardButton
              onClick={() => {
                buttonAction();
              }}
              variant="WHITE"
              iconRight={<StarsIcon />}
            >
              <span>{pondooStatusBarObject.buttonTitle}</span>
            </StandardButton>
          )}
          {(pondooStatusBarObject.status === "Running" ||
            pondooStatusBarObject.status === "NotScheduled" ||
            pondooStatusBarObject.status === "Scheduled") && (
            <div className="flex items-center">
              <ProgressBar progressBarValue={progressBarValue}></ProgressBar>
              <IconButtonComponent
                id="x"
                icon={<XIcon />}
                onClick={abortOptimization}
              ></IconButtonComponent>
            </div>
          )}
        </>
      )}
    </BlackRoundWrapper>
  );
};

export default React.memo(PondooPlanStatusBar);
